import { Navigate, Route, Routes } from 'react-router-dom';
import { ROUTES, LIVE_SURFACES } from '@/config/routes';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

import { LandingPage } from '@/modules/app/pages/LandingPage';
import { LoginPage } from '@/modules/app/pages/LoginPage';
import { SignupPage } from '@/modules/app/pages/SignupPage';
import { ProfileBuilderPage } from '@/modules/app/pages/ProfileBuilderPage';
import { VerifyAgePage } from '@/modules/app/pages/VerifyAgePage';
import { AuthCallbackPage } from '@/modules/app/pages/AuthCallbackPage';
import { AuthHopPage } from '@/auth/AuthHopPage';
import { ForgotPasswordPage } from '@/modules/app/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/modules/app/pages/ResetPasswordPage';
import { AdminQueuePage } from '@/modules/admin/pages/AdminQueuePage';
import { AdminSafetyPage } from '@/modules/admin/pages/AdminSafetyPage';
import { AdminRevenuePage } from '@/modules/admin/pages/AdminRevenuePage';
import { AdminLaunchGatesPage } from '@/modules/admin/pages/AdminLaunchGatesPage';
import { AdminSecretsPage } from '@/modules/admin/pages/AdminSecretsPage';
import { MfaChallengePage } from '@/modules/auth/pages/MfaChallengePage';
import { LogoutPage } from '@/modules/app/pages/LogoutPage';
import { VerifyPage } from '@/modules/app/pages/VerifyPage';
import { PlansPage } from '@/modules/app/pages/PlansPage';
import { FeedPage } from '@/modules/app/pages/FeedPage';
import { GridPage } from '@/modules/app/pages/GridPage';
import { MapPage } from '@/modules/app/pages/MapPage';
import { ProfilePage } from '@/modules/app/pages/ProfilePage';
import { MessagesPage } from '@/modules/app/pages/MessagesPage';
import { SettingsPage } from '@/modules/app/pages/SettingsPage';
import { WorldPage } from '@/modules/world/pages/WorldPage';
import { LegalPage } from '@/modules/legal/LegalPage';
import { HottiePage } from '@/modules/hottie/pages/HottiePage';

// HOTTR.vip — directory-only module. See docs/HOTTR_VIP_PRODUCT_RULES.md.
import { VipLandingPage } from '@/modules/vip/pages/VipLandingPage';
import { VipAccessPage } from '@/modules/vip/pages/VipAccessPage';
import { VipRosterPage } from '@/modules/vip/pages/VipRosterPage';
import { VipProfilePage } from '@/modules/vip/pages/VipProfilePage';
import { VipContactPage } from '@/modules/vip/pages/VipContactPage';
import { VipProviderApplyPage } from '@/modules/vip/pages/VipProviderApplyPage';
import { VipProviderProfilePage } from '@/modules/vip/pages/VipProviderProfilePage';
import { VipVerifyPage } from '@/modules/vip/pages/VipVerifyPage';
import { VipWorldPage } from '@/modules/vip/pages/VipWorldPage';

// HOTTR.tv — streaming + creator surface. See docs/HOTTR_TV_PRODUCT_RULES.md.
import { TvBrowsePage } from '@/modules/tv/pages/TvBrowsePage';
import { TvWatchPage } from '@/modules/tv/pages/TvWatchPage';
import { TvSeriesDetailPage } from '@/modules/tv/pages/TvSeriesDetailPage';
import { TvCreatorPage } from '@/modules/tv/pages/TvCreatorPage';
import { TvLibraryPage } from '@/modules/tv/pages/TvLibraryPage';
import { TvLockedRoomPage } from '@/modules/tv/pages/TvLockedRoomPage';
import { TvProfilePage } from '@/modules/tv/pages/TvProfilePage';
import { TvAccountPage } from '@/modules/tv/pages/TvAccountPage';
import { TvCreatorApplyPage } from '@/modules/tv/pages/TvCreatorApplyPage';
import { TvWorldPage } from '@/modules/tv/pages/TvWorldPage';

/**
 * Route table.
 *
 * Public surfaces (no auth required):
 *   /              — Landing with LOG IN + SIGN UP
 *   /login         — log in form
 *   /signup        — signup form
 *   /auth/callback — OAuth / Passport SSO return
 *   /auth/hop      — start Passport SSO on this origin (world switch)
 *
 * Auth-only — needs a session but profile may still be incomplete:
 *   /profile-builder — mandatory first-run builder
 *
 * Protected — requires session AND complete profile:
 *   /feed, /grid, /map, /messages, /world, /settings, /profile/:handle
 *   plus all /vip/* and /tv/* surfaces
 *
 * Legacy redirects:
 *   /enter, /onboarding, /discover, /vip-preview
 */
export const AppRoutes = () => (
  <Routes>
    {/* Public */}
    <Route path={ROUTES.landing} element={<LandingPage />} />
    <Route path={ROUTES.login} element={<LoginPage />} />
    <Route path={ROUTES.signup} element={<SignupPage />} />
    <Route path={ROUTES.authCallback} element={<AuthCallbackPage />} />
    <Route path={ROUTES.authHop} element={<AuthHopPage />} />
    <Route path={ROUTES.forgot} element={<ForgotPasswordPage />} />
    <Route path={ROUTES.reset} element={<ResetPasswordPage />} />
    <Route path={ROUTES.mfaChallenge} element={<MfaChallengePage />} />
    <Route path={ROUTES.logout} element={<LogoutPage />} />

    {/* Legal — public, reachable without auth or age-gate */}
    <Route path={ROUTES.legal()} element={<LegalPage />} />

    {/* The ecosystem's one Hottie. Sibling sites link here on hold-the-Orb. */}
    <Route path={ROUTES.hottie} element={<HottiePage />} />

    {/* Auth-only — profile may still be incomplete */}
    <Route
      path={ROUTES.profileBuilder}
      element={
        <ProtectedRoute requireComplete={false}>
          <ProfileBuilderPage />
        </ProtectedRoute>
      }
    />

    {/* Hard age-gate entry — must be reachable before age verification */}
    <Route
      path={ROUTES.verifyAge}
      element={
        <ProtectedRoute requireComplete={false} requireAgeVerified={false}>
          <VerifyAgePage />
        </ProtectedRoute>
      }
    />

    {/* Protected — session + complete profile required */}
    <Route
      path={ROUTES.feed}
      element={
        <ProtectedRoute>
          <FeedPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.grid}
      element={
        <ProtectedRoute>
          <GridPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.map}
      element={
        <ProtectedRoute>
          <MapPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.profile()}
      element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.messages}
      element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.world}
      element={
        <ProtectedRoute>
          <WorldPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.settings}
      element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      }
    />

    {/* HOTTR.vip — hidden until backend ships (B-series); LIVE_SURFACES gate */}
    {LIVE_SURFACES.vip && (
      <>
    <Route
      path={ROUTES.vip.landing}
      element={
        <ProtectedRoute>
          <VipLandingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.vip.access}
      element={
        <ProtectedRoute>
          <VipAccessPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.vip.roster}
      element={
        <ProtectedRoute>
          <VipRosterPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.vip.profile()}
      element={
        <ProtectedRoute>
          <VipProfilePage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.vip.contact()}
      element={
        <ProtectedRoute>
          <VipContactPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.vip.providerApply}
      element={
        <ProtectedRoute>
          <VipProviderApplyPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.vip.providerProfile}
      element={
        <ProtectedRoute>
          <VipProviderProfilePage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.vip.verify}
      element={
        <ProtectedRoute>
          <VipVerifyPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.vip.world}
      element={
        <ProtectedRoute>
          <VipWorldPage />
        </ProtectedRoute>
      }
    />

      </>
    )}

    {/* HOTTR.tv — hidden until backend ships (B-series); LIVE_SURFACES gate */}
    {LIVE_SURFACES.tv && (
      <>
    <Route
      path={ROUTES.tv.browse}
      element={
        <ProtectedRoute>
          <TvBrowsePage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.tv.watch()}
      element={
        <ProtectedRoute>
          <TvWatchPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.tv.series()}
      element={
        <ProtectedRoute>
          <TvSeriesDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.tv.creatorApply}
      element={
        <ProtectedRoute>
          <TvCreatorApplyPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.tv.creator()}
      element={
        <ProtectedRoute>
          <TvCreatorPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.tv.library}
      element={
        <ProtectedRoute>
          <TvLibraryPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.tv.locked()}
      element={
        <ProtectedRoute>
          <TvLockedRoomPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.tv.profile}
      element={
        <ProtectedRoute>
          <TvProfilePage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.tv.account}
      element={
        <ProtectedRoute>
          <TvAccountPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.tv.world}
      element={
        <ProtectedRoute>
          <TvWorldPage />
        </ProtectedRoute>
      }
    />
      </>
    )}

    <Route
      path={ROUTES.verify}
      element={
        <ProtectedRoute>
          <VerifyPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.plans}
      element={
        <ProtectedRoute>
          <PlansPage />
        </ProtectedRoute>
      }
    />

    {/* Admin — moderator-only */}
    <Route
      path={ROUTES.admin.queue}
      element={
        <ProtectedRoute requireModerator requireComplete={false} requireAgeVerified={false}>
          <AdminQueuePage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.admin.safety}
      element={
        <ProtectedRoute requireModerator requireComplete={false} requireAgeVerified={false}>
          <AdminSafetyPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.admin.revenue}
      element={
        <ProtectedRoute requireModerator requireComplete={false} requireAgeVerified={false}>
          <AdminRevenuePage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.admin.launchGates}
      element={
        <ProtectedRoute requireModerator requireComplete={false} requireAgeVerified={false}>
          <AdminLaunchGatesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTES.admin.secrets}
      element={
        <ProtectedRoute requireModerator requireComplete={false} requireAgeVerified={false}>
          <AdminSecretsPage />
        </ProtectedRoute>
      }
    />

    {/* Legacy redirects */}
    <Route path="/enter" element={<Navigate to={ROUTES.signup} replace />} />
    <Route path="/onboarding" element={<Navigate to={ROUTES.profileBuilder} replace />} />
    <Route path="/discover" element={<Navigate to={ROUTES.feed} replace />} />
    <Route path={ROUTES.vipPreview} element={<Navigate to={ROUTES.vip.landing} replace />} />
    <Route path="*" element={<Navigate to={ROUTES.landing} replace />} />
  </Routes>
);
